import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import useConfig from "../../../actions/Tenants/config/configHook";
import { callAvosApi } from "../../../utils/useAvosApiHook";
import { UseExportReturn, UseImportExportProps } from "./types";
import { exportDataToExcel, generateExcelFileName } from "./utils";

export const useExport = ({ entityName, customEndpoint, excludeFields = ["id"] }: Omit<UseImportExportProps, "affectedQueries">): UseExportReturn => {
    const config = useConfig();

    const queryKey = `all-${entityName}s`;
    const endpoint = customEndpoint || `/${entityName}/`;

    const getAllItems = async () => {
        const res = await callAvosApi(endpoint);
        return res.data;
    };

    const { data: allItems } = useQuery({
        queryKey: [queryKey],
        queryFn: () => getAllItems(),
    });

    const formattedData = useMemo(() => {
        const rows: any[] = [];

        (allItems?.results || []).forEach((item) => {
            const row = Object.keys(item)?.reduce((acc: any, k: string) => {
                const value = item[k];
                if (!excludeFields.includes(k) && value !== undefined && value !== null) {
                    const formattedValue = Array.isArray(value) ? value.join(", ") : value;

                    acc[k] = formattedValue;
                }

                return acc;
            }, {});

            rows.push(row);

            // for client and supplier exports, to include email recipients in the export
            if (item.infos) {
                item.infos?.forEach((info: any) => {
                    const emailRecipients = info.email_recipients?.to;

                    // cc and bcc should be for internal emails only
                    if (!emailRecipients?.length) return;

                    const newRow: any = {
                        name: item.name,
                        emails: emailRecipients.map((e) => e.address).join(", "),
                    };

                    if (info.fruit_type) {
                        newRow.fruit_types = info.fruit_type;
                    }

                    if (info.report_type) {
                        newRow.report_type = info.report_type;
                    }

                    rows.push(newRow);
                });
            }

            // for supplier exports
            if (item.growers) {
                const growers = Object.values(item.growers || {}).flatMap((g: any) => g);

                growers.forEach((grower) => {
                    const newRow: any = {
                        name: item.name,
                        fruit_types: grower.fruit_type,
                        grower: grower.grower_ggn,
                    };

                    rows.push(newRow);
                });
            }
        });

        return rows;
    }, [allItems, config]);

    const headers = useMemo(() => {
        if (!formattedData?.length) return [];
        const keySet = new Set<string>();
        formattedData.forEach((row) => Object.keys(row).forEach((k) => keySet.add(k)));
        return Array.from(keySet);
    }, [formattedData]);

    const onExportData = async () => {
        exportDataToExcel(formattedData, generateExcelFileName(`${entityName}s`), undefined, headers);
    };

    return {
        onExportData: allItems?.count > 0 && onExportData,
    };
};
